import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart, BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend,
} from "chart.js";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  BarChart3, Wallet, TrendingUp, Download, AlertCircle, ChevronLeft, ChevronRight,
} from "lucide-react";
import { getAdminRevenueReport, getAdminInstallmentsReport } from "../../services/zumaleAdminReports";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import StatTile from "../../components/StatTile";

Chart.register(BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend);

/* ---------- helpers ---------- */
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtFull = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "—");
const apiDate = (d) => moment(d).format("YYYY-MM-DD");

const STREAMS = [
  { key: "DEVELOPER", label: "Developer", color: "#f75828" },
  { key: "ID_SUBSCRIPTION", label: "ID Subscription", color: "#6366f1" },
  { key: "INTERIOR_EXECUTION", label: "Interior Execution", color: "#10b981" },
  { key: "REFERRAL_COMMISSION", label: "Referral Commission", color: "#94a3b8", upcoming: true },
  { key: "MOVE_IN_SERVICES", label: "Move-in Services", color: "#94a3b8", upcoming: true },
];
const STREAM_COLOR = Object.fromEntries(STREAMS.map((s) => [s.key, s.color]));

const PERIODS = [
  { value: "month", label: "Last month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
  { value: "2years", label: "2 years" },
  { value: "custom", label: "Custom" },
];

const periodRange = (value) => {
  const end = moment().endOf("day").toDate();
  const map = {
    month: moment().subtract(1, "month"),
    quarter: moment().subtract(1, "quarter"),
    year: moment().subtract(1, "year"),
    "2years": moment().subtract(2, "years"),
  };
  return { start: (map[value] || moment().subtract(1, "month")).startOf("day").toDate(), end };
};

const downloadCsv = (filename, headers, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map((h) => esc(h.label)).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => esc(r[h.key])).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ---------- shared small components ---------- */
const SectionCard = ({ title, children, action }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    {title && (
      <div className="px-5 pt-5 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

/* ---------- period filter ---------- */
const PeriodFilter = ({ period, setPeriod, startDate, endDate, setStartDate, setEndDate }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
      {PERIODS.map((p) => (
        <button key={p.value} onClick={() => setPeriod(p.value)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            period === p.value ? "bg-flamingo-500 text-white" : "text-slate-600 hover:bg-slate-50"
          }`}>
          {p.label}
        </button>
      ))}
    </div>
    {period === "custom" && (
      <div className="flex items-center gap-2">
        <DatePicker selected={startDate} onChange={setStartDate} maxDate={endDate || new Date()}
          dateFormat="dd MMM yyyy"
          className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 w-32" />
        <span className="text-slate-400 text-xs">to</span>
        <DatePicker selected={endDate} onChange={setEndDate} minDate={startDate} maxDate={new Date()}
          dateFormat="dd MMM yyyy"
          className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 w-32" />
      </div>
    )}
  </div>
);

/* ---------- Revenue tab ---------- */
const RevenueTab = ({ startDate, endDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(["DEVELOPER", "ID_SUBSCRIPTION", "INTERIOR_EXECUTION"]);

  const toggleStream = (key, upcoming) => {
    if (upcoming) return;
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getAdminRevenueReport({ startDate: apiDate(startDate), endDate: apiDate(endDate), streams: selected })
      .then((res) => mounted && setData(res))
      .catch((e) => mounted && setError(e?.response?.data?.detail || "Failed to load revenue"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [startDate, endDate, selected]);

  const activeStreams = (data?.streams || []).filter((s) => !s.upcoming && selected.includes(s.key));

  const barData = useMemo(() => ({
    labels: (data?.series || []).map((p) => p.bucket),
    datasets: activeStreams.map((s) => ({
      label: s.label,
      data: (data?.series || []).map((p) => Number(p.amounts?.[s.key] || 0)),
      backgroundColor: STREAM_COLOR[s.key],
      stack: "rev",
      borderRadius: 3,
    })),
  }), [data, activeStreams]);

  const donutData = useMemo(() => ({
    labels: activeStreams.map((s) => s.label),
    datasets: [{ data: activeStreams.map((s) => s.total), backgroundColor: activeStreams.map((s) => STREAM_COLOR[s.key]), borderWidth: 0 }],
  }), [activeStreams]);

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: true, position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtFull(c.parsed.y ?? c.parsed)}` } } },
    scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true, ticks: { callback: (v) => fmtINR(v) } } },
  };
  const donutOpts = {
    responsive: true, maintainAspectRatio: false, cutout: "62%",
    plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${fmtFull(c.parsed)}` } } },
  };

  const exportCsv = () => {
    const headers = [{ key: "bucket", label: "Period" }, ...activeStreams.map((s) => ({ key: s.key, label: s.label })), { key: "total", label: "Total" }];
    const rows = (data?.series || []).map((p) => {
      const row = { bucket: p.bucket };
      let total = 0;
      activeStreams.forEach((s) => { const v = Number(p.amounts?.[s.key] || 0); row[s.key] = v; total += v; });
      row.total = total;
      return row;
    });
    downloadCsv(`revenue_${apiDate(startDate)}_${apiDate(endDate)}.csv`, headers, rows);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* stream chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {STREAMS.map((s) => {
          const on = !s.upcoming && selected.includes(s.key);
          return (
            <button key={s.key} onClick={() => toggleStream(s.key, s.upcoming)} disabled={s.upcoming}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                s.upcoming ? "border-slate-200 text-slate-300 cursor-not-allowed"
                  : on ? "border-transparent text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              style={on ? { backgroundColor: s.color } : undefined}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.upcoming ? "#cbd5e1" : s.color }} />
              {s.label}{s.upcoming && " · Soon"}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile icon={Wallet} accent="flamingo" label="Total revenue"
          value={loading ? "…" : fmtINR(data?.grandTotal)} subtitle={`${fmtDate(startDate)} – ${fmtDate(endDate)}`} />
        {(data?.streams || []).filter((s) => selected.includes(s.key) && !s.upcoming).map((s, i) => (
          <StatTile key={s.key} icon={TrendingUp} accent={["emerald", "indigo", "slate"][i % 3]}
            label={s.label} value={loading ? "…" : fmtINR(s.total)} />
        ))}
      </div>

      {/* charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <SectionCard title="Revenue trend"
            action={<button onClick={exportCsv} className="inline-flex items-center gap-1 text-xs font-semibold text-flamingo-600 hover:text-flamingo-700"><Download size={13} /> CSV</button>}>
            <div style={{ height: 320 }}>
              {activeStreams.length ? <Bar data={barData} options={chartOpts} /> : <p className="text-sm text-slate-400 text-center pt-24">Select a revenue stream.</p>}
            </div>
          </SectionCard>
        </div>
        <SectionCard title="Stream mix">
          <div style={{ height: 320 }}>
            {activeStreams.some((s) => s.total > 0) ? <Doughnut data={donutData} options={donutOpts} /> : <p className="text-sm text-slate-400 text-center pt-24">No revenue in range.</p>}
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

/* ---------- Installments tab ---------- */
const STATUS_PILL = {
  PAID: "bg-emerald-50 text-emerald-700",
  OVERDUE: "bg-rose-50 text-rose-600",
  FUTURE: "bg-amber-50 text-amber-700",
};
const TYPE_LABEL = { DEVELOPER: "Developer", ID_EXECUTION: "Buyer ID Execution", ID_SUBSCRIPTION: "ID Subscription" };

const InstallmentsTab = ({ startDate, endDate }) => {
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [type, setType] = useState(""); // "" = all
  const [status, setStatus] = useState(""); // "" = all
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const gridRef = useRef(null);

  const fetchParams = useCallback((overrides = {}) => ({
    startDate: apiDate(startDate), endDate: apiDate(endDate),
    types: type ? [type] : undefined, status: status || undefined,
    page, pageSize, ...overrides,
  }), [startDate, endDate, type, status, page]);

  useEffect(() => { setPage(1); }, [startDate, endDate, type, status]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getAdminInstallmentsReport(fetchParams())
      .then((res) => mounted && setResp(res))
      .catch((e) => mounted && setError(e?.response?.data?.detail || "Failed to load installments"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [fetchParams]);

  const columnDefs = useMemo(() => [
    { headerName: "Type", field: "installmentType", width: 150, valueFormatter: (p) => TYPE_LABEL[p.value] || p.value },
    { headerName: "Payer", field: "payerName", flex: 1, minWidth: 140 },
    { headerName: "Context", field: "context", flex: 1, minWidth: 160 },
    { headerName: "Description", field: "description", flex: 1, minWidth: 140 },
    { headerName: "Amount", field: "amount", width: 120, valueFormatter: (p) => fmtFull(p.value), type: "rightAligned" },
    { headerName: "Received", field: "receivedAmount", width: 120, valueFormatter: (p) => (p.value == null ? "—" : fmtFull(p.value)), type: "rightAligned" },
    { headerName: "Due", field: "dueDate", width: 120, valueFormatter: (p) => fmtDate(p.value) },
    { headerName: "Paid", field: "paidDate", width: 120, valueFormatter: (p) => fmtDate(p.value) },
    {
      headerName: "Status", field: "status", width: 110,
      cellRenderer: (p) => (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_PILL[p.value] || "bg-slate-100 text-slate-600"}`}>{p.value}</span>
      ),
    },
  ], []);

  const exportCsv = async () => {
    const res = await getAdminInstallmentsReport(fetchParams({ page: 1, pageSize: 1000 }));
    const headers = [
      { key: "installmentType", label: "Type" }, { key: "payerName", label: "Payer" },
      { key: "context", label: "Context" }, { key: "description", label: "Description" },
      { key: "amount", label: "Amount" }, { key: "receivedAmount", label: "Received" },
      { key: "dueDate", label: "Due" }, { key: "paidDate", label: "Paid" }, { key: "status", label: "Status" },
    ];
    const rows = (res?.items || []).map((r) => ({ ...r, installmentType: TYPE_LABEL[r.installmentType] || r.installmentType }));
    downloadCsv(`installments_${apiDate(startDate)}_${apiDate(endDate)}.csv`, headers, rows);
  };

  const totalPages = resp?.totalPages || 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="rounded-lg border border-slate-300 text-xs py-1.5 px-2 bg-white">
          <option value="">All types</option>
          <option value="DEVELOPER">Developer Installments</option>
          <option value="ID_EXECUTION">Buyer ID Execution</option>
          <option value="ID_SUBSCRIPTION">ID Subscription</option>
        </select>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
          {["", "PAID", "OVERDUE", "FUTURE"].map((s) => (
            <button key={s || "ALL"} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${status === s ? "bg-flamingo-500 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-flamingo-600 hover:text-flamingo-700">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="ag-theme-quartz rounded-2xl overflow-hidden border border-slate-200" style={{ height: 520 }}>
        <AgGridReact ref={gridRef} rowData={resp?.items || []} columnDefs={columnDefs}
          loading={loading} suppressCellFocus headerHeight={40} rowHeight={44} />
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{loading ? "Loading…" : `${resp?.total ?? 0} installment(s)`}</span>
        <div className="flex items-center gap-2">
          <button disabled={!resp?.hasPrevious} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={14} /> Prev</button>
          <span>Page {resp?.page ?? 1} of {totalPages}</span>
          <button disabled={!resp?.hasNext} onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 disabled:opacity-40 hover:bg-slate-50">Next <ChevronRight size={14} /></button>
        </div>
      </div>
    </div>
  );
};

/* ---------- page ---------- */
const ZumaleAdminReports = () => {
  const [tab, setTab] = useState("revenue");
  const [period, setPeriod] = useState("year");
  const initial = periodRange("year");
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);

  useEffect(() => {
    if (period !== "custom") {
      const r = periodRange(period);
      setStartDate(r.start);
      setEndDate(r.end);
    }
  }, [period]);

  const TABS = [
    { key: "revenue", label: "Revenue" },
    { key: "installments", label: "Installments" },
  ];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={BarChart3}
        title="Reports"
        subtitle="Business metrics & health"
        actions={
          <PeriodFilter period={period} setPeriod={setPeriod}
            startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
        }
      />

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <Tabs tabs={TABS} active={tab} onChange={setTab} />
        <div className="p-4 lg:p-5">
          {tab === "revenue"
            ? <RevenueTab startDate={startDate} endDate={endDate} />
            : <InstallmentsTab startDate={startDate} endDate={endDate} />}
        </div>
      </div>
    </div>
  );
};

export default ZumaleAdminReports;
